/**
 * A channel's draft quote, as the merch CSR who owns it tidies it before
 * publishing (merch spec 12 §6 "Quantity options" and "Alternatives"; brief
 * 01 §11). Headless: the words and the form's arithmetic, the same whichever
 * shell renders them.
 *
 * What is still "to check" is read straight off the view: a line with
 * quantity candidates (Promo Office's price-list breaks, not quoted by the
 * team) and a line flagged as a possible alternative. Deciding either clears
 * it, because a staff save writes only what staff decided. A thin price is a
 * warning to read, never a check to clear: the team's price stands (Ben,
 * 14 Oct: "flag if thin, never reprice"). The channel judges it when it
 * writes the draft (Daniel, 19 Oct).
 */

import type {
  ChosenQuantity,
  QuoteAddressChoice,
  QuoteCandidateView,
  QuoteLinePriceCheck,
  QuoteLineView,
  QuoteView,
} from "../data/quote-types";
import { penceOfPounds } from "./delivery-selection";
import { formatQuotedPrice } from "./formatters";

const count = (n: number) => n.toLocaleString("en-GB");

/** A draft made by a channel (Promo Office), with the channel's document it came from. */
export function isChannelDraft(quote: QuoteView): boolean {
  return quote.status === "draft" && !!quote.made_from_label;
}

export interface DraftChecks {
  /** Lines with price-list quantities still to choose from. */
  quantities: QuoteLineView[];
  /** Lines that repeat an article, still to decide. */
  alternatives: QuoteLineView[];
}

/** What is left to decide on a draft, line by line; empty for any other quote. */
export function draftChecksOf(quote: QuoteView): DraftChecks {
  if (quote.status !== "draft") return { quantities: [], alternatives: [] };
  return {
    quantities: quote.lines.filter(
      (l) => (l.quantity_candidates ?? []).length > 0,
    ),
    alternatives: quote.lines.filter((l) => l.is_possible_alternative),
  };
}

function plural(n: number, one: string, many: string): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}

/** "quantities on 2 lines": the action box's thing after "Choose". */
export function quantitiesRowWords(n: number): string {
  return `quantities on ${plural(n, "line", "lines")}`;
}

/** "1 possible alternative": the action box's thing after "Decide on". */
export function alternativesRowWords(n: number): string {
  return plural(n, "possible alternative", "possible alternatives");
}

/**
 * The draft's "to check" summary: "2 lines with price-list quantities to
 * choose from and 1 line that may be an alternative". Null when nothing is
 * left to decide.
 */
export function describeDraftChecks(checks: DraftChecks): string | null {
  const parts = [
    ...(checks.quantities.length > 0
      ? [
          `${plural(checks.quantities.length, "line", "lines")} with price-list quantities to choose from`,
        ]
      : []),
    ...(checks.alternatives.length > 0
      ? [
          `${plural(checks.alternatives.length, "line", "lines")} that may be an alternative`,
        ]
      : []),
  ];
  return parts.length > 0 ? parts.join(" and ") : null;
}

/** "£2.10 each", to a tenth of a penny where the price needs it. */
export function unitPriceWords(pricePence: number, quantity: number): string {
  return `${new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  }).format(pricePence / quantity / 100)} each`;
}

/** The price list's quantities on a line, in one line of text: "250 at £575.00 · 1,000 at £1,900.00". */
export function candidatesWords(candidates: QuoteCandidateView[]): string {
  return [...candidates]
    .sort((a, b) => a.quantity - b.quantity)
    .map((c) => `${count(c.quantity)} at ${formatQuotedPrice(c.price_pence)}`)
    .join(" · ");
}

/**
 * The line a possible alternative repeats, as staff read it: ".01 500 ×
 * Notebooks, A5, debossed logo". Null when the view does not say which.
 */
export function repeatedLineOf(
  quote: QuoteView,
  line: QuoteLineView,
): QuoteLineView | null {
  if (!line.repeats_line_ref) return null;
  return quote.lines.find((l) => l.line_ref === line.repeats_line_ref) ?? null;
}

/**
 * A thin price in words, after the channel's verdict (the guardrails' words).
 * A warning: it says the team's price stands and publishing is not stopped.
 * Null for a price the guardrails passed.
 */
export function describeThinPrice(
  check: QuoteLinePriceCheck,
  pricePence: number,
): { tone: "warning" | "danger"; headline: string; detail: string } | null {
  if (check.verdict === "ok") return null;
  const below = check.policy_price_pence - pricePence;
  const verdict =
    check.verdict === "blocked"
      ? "Below the price group's hard limit"
      : "Needs approval";
  const reason = check.verdict_reason ? `: ${check.verdict_reason}` : "";
  const margin =
    check.margin_pct === null
      ? `The team's price is against a buy price of ${formatQuotedPrice(check.cost_pence)}.`
      : `The team's price leaves a ${check.margin_pct}% margin on a buy price of ${formatQuotedPrice(check.cost_pence)}.`;
  return {
    tone: check.verdict === "blocked" ? "danger" : "warning",
    headline:
      below > 0
        ? `Thin price: ${formatQuotedPrice(below)} below One's policy price of ${formatQuotedPrice(check.policy_price_pence)}`
        : `Thin price against One's policy price of ${formatQuotedPrice(check.policy_price_pence)}`,
    detail: `${margin} ${verdict}${reason}. The team's price stands, and publishing is not stopped.`,
  };
}

/** The line's thin price in words, when the channel flagged it; null otherwise. */
export function thinPriceOf(
  line: QuoteLineView,
): ReturnType<typeof describeThinPrice> {
  return line.price_check && line.price_pence !== null
    ? describeThinPrice(line.price_check, line.price_pence)
    : null;
}

/** The customer's address a channel's draft's goods go to now, among those it may go to; null when not listed. */
export function deliveryAddressOf(quote: QuoteView): QuoteAddressChoice | null {
  return (
    (quote.delivery_address_choices ?? []).find(
      (a) => a.address_id === quote.delivery_address_id,
    ) ?? null
  );
}

// ── Choosing the quantities to offer ───────────────────────────────────────

export type QuantityRowSource = "team" | "price_list" | "added";

/** One row of the Choose quantities form: a quantity, its price as typed, and whether it is offered. */
export interface QuantityRow {
  key: string;
  source: QuantityRowSource;
  /** As typed: a whole number. */
  quantity: string;
  /** As typed: pounds, "1,050.00". */
  price: string;
  /** The buy price the team or the price list gives; null when none is known here (the team's is kept by quotes). */
  costPence: number | null;
  /** As typed, for a row with no known buy price: pounds. Empty keeps whatever quotes holds. */
  cost: string;
  isOffered: boolean;
}

/** A row whose buy price the merch CSR types: the price list gives none, or they added the quantity. */
export function needsTypedCost(row: QuantityRow): boolean {
  return row.source !== "team" && row.costPence === null;
}

/** "Quoted by the team", or the candidate's own source label, or "Added by you". */
export function quantityRowSourceWords(
  row: QuantityRow,
  priceListLabel: string,
): string {
  switch (row.source) {
    case "team":
      return "Quoted by the team";
    case "price_list":
      return priceListLabel;
    case "added":
      return "Added by you";
  }
}

/** A waiting quantity (no supplier price chosen yet) starts the form's price blank. */
function poundsText(pence: number | null): string {
  if (pence === null) return "";
  return (pence / 100).toFixed(2);
}

/**
 * The form's starting rows: the team's quantities (the line's own and any
 * options already on it) offered, the price list's not, smallest first.
 */
export function quantityRowsOf(line: QuoteLineView): QuantityRow[] {
  const team =
    line.quantity_options.length > 0
      ? line.quantity_options.map((o) => ({
          quantity: o.quantity,
          pricePence: o.price_pence,
        }))
      : [{ quantity: line.quantity, pricePence: line.price_pence ?? 0 }];
  const teamQuantities = new Set(team.map((t) => t.quantity));
  const rows: QuantityRow[] = [
    ...team.map((t) => ({
      key: `team-${t.quantity}`,
      source: "team" as const,
      quantity: String(t.quantity),
      price: poundsText(t.pricePence),
      costPence: null,
      cost: "",
      isOffered: true,
    })),
    ...(line.quantity_candidates ?? [])
      // The team's quote wins where both give a price for one quantity.
      .filter((c) => !teamQuantities.has(c.quantity))
      .map((c) => ({
        key: `price-list-${c.quantity}`,
        source: "price_list" as const,
        quantity: String(c.quantity),
        price: poundsText(c.price_pence),
        costPence: c.cost_pence,
        cost: "",
        isOffered: false,
      })),
  ];
  return rows.sort((a, b) => Number(a.quantity) - Number(b.quantity));
}

export interface QuantityRowProblem {
  quantity?: string;
  price?: string;
  cost?: string;
}

/** What is wrong with each offered row, by its key, in the words shown under its input. */
export function quantityRowProblems(
  rows: QuantityRow[],
): Record<string, QuantityRowProblem> {
  const problems: Record<string, QuantityRowProblem> = {};
  const offered = rows.filter((r) => r.isOffered);
  for (const row of offered) {
    const problem: QuantityRowProblem = {};
    const quantity = Number(row.quantity.replace(/,/g, ""));
    if (!/^[\d,]+$/.test(row.quantity.trim()) || quantity <= 0)
      problem.quantity = "Enter a whole number above 0.";
    else if (
      offered.find((r) => Number(r.quantity.replace(/,/g, "")) === quantity)
        ?.key !== row.key
    )
      problem.quantity = "Each quantity is offered once.";
    const pence = penceOfPounds(row.price);
    if (pence === null || pence <= 0) problem.price = "Enter a price above £0.";
    const cost = row.cost.trim() ? penceOfPounds(row.cost) : undefined;
    if (cost === null || (cost !== undefined && cost <= 0))
      problem.cost = "Enter a buy price above £0.";
    if (problem.quantity || problem.price || problem.cost)
      problems[row.key] = problem;
  }
  return problems;
}

/**
 * The offered rows as the step takes them, smallest first: a typed buy price is sent, else none, so quotes keeps the
 * team's or the price list's. Assumes `quantityRowProblems` found none.
 */
export function chosenQuantitiesOf(rows: QuantityRow[]): ChosenQuantity[] {
  return rows
    .filter((r) => r.isOffered)
    .map((r) => ({
      quantity: Number(r.quantity.replace(/,/g, "")),
      price_pence: penceOfPounds(r.price) ?? 0,
      ...(r.cost.trim() ? { cost_pence: penceOfPounds(r.cost) ?? 0 } : {}),
    }))
    .sort((a, b) => a.quantity - b.quantity);
}

/**
 * What the line will offer, in words, as the form stands: the smallest
 * offered is the line's own quantity (quotes lists options from the line's
 * own up), the rest are options the customer picks between.
 */
export function describeOffer(chosen: ChosenQuantity[]): string {
  if (chosen.length === 0)
    return "Tick at least one quantity: the line needs its own.";
  const [own, ...options] = chosen;
  const ownWords = `${count(own!.quantity)} at ${formatQuotedPrice(own!.price_pence ?? 0)}`;
  if (options.length === 0)
    return `The line is ${ownWords}, with no quantity options.`;
  return `The line is ${ownWords} (the smallest offered is the line's own), with ${options
    .map(
      (o) => `${count(o.quantity)} at ${formatQuotedPrice(o.price_pence ?? 0)}`,
    )
    .join(
      " and ",
    )} as ${options.length === 1 ? "an option" : "options"}. The customer picks one as they accept.`;
}

// ── Blocked commands ───────────────────────────────────────────────────────

/** Why quotes refuses one of its commands on the quote now ("hide or block"), or null when it is offered or hidden. */
export function blockedCommandReason(
  quote: QuoteView,
  name: string,
): string | null {
  const command = quote.commands.find((c) => c.name === name);
  return command && !command.enabled ? (command.reason ?? null) : null;
}
